import { useEffect, useState } from "react";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import { Label } from "@/project/karuna/components/ui/label"; // @ts-ignore - label is not a valid module
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/project/karuna/components/ui/input-otp"; // @ts-ignore - input-otp is not a valid module
import { useNavigate } from "react-router-dom";
import { GraduationCap, ArrowLeft, Loader2 } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { z } from "zod";
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import OTPService from "@/services/APIs/OTPService";
import {
  SCHOOL_DEFAULT_PASSWORD,
  getKarunaPhoneCode,
} from "@/project/karuna/utils/schoolTenantUser";
import { TenantUserService } from "@/services/APIs";
import {
  getAccessToken,
  getUserInfo,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
} from "@/lib/storeManager";

// Helper function to normalize mobile number (remove spaces, ensure 10 digits)
const normalizeMobileNumber = (mobileNumber: string): string => {
  return mobileNumber.replace(/\D/g, "").slice(0, 10);
};

const loginSchema = z.object({
  mobileNumber: z
    .string()
    .trim()
    .regex(/^[6-9]\d{9}$/, {
      message: "Invalid mobile number. Must be 10 digits starting with 6-9",
    }),
  otp: z
    .string()
    .length(6, { message: "OTP must be 6 digits" })
    .regex(/^\d{6}$/, { message: "OTP must contain only digits" }),
});

const SchoolLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const getLink = useKarunaLink();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = getAccessToken();
    const userInfo = getUserInfo();
    if (token && userInfo?.phone_number) {
      navigate(getLink("/school/dashboard"));
    }
  }, [navigate, getLink]);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  const [formData, setFormData] = useState({
    mobileNumber: "",
    otp: "",
  });
  const [codeReference, setCodeReference] = useState<string | null>(null);

  const handleSendOTP = async () => {
    setLoading(true);
    try {
      // Validate mobile number
      z.object({
        mobileNumber: z
          .string()
          .trim()
          .regex(/^[6-9]\d{9}$/, {
            message:
              "Invalid mobile number. Must be 10 digits starting with 6-9",
          }),
      }).parse({
        mobileNumber: formData.mobileNumber,
      });

      // Normalize mobile number
      const normalizedMobile = normalizeMobileNumber(formData.mobileNumber);

      const phoneCode = getKarunaPhoneCode();

      // Send OTP via login API (POST /api/auth/login/)
      const response = await OTPService.sendOTPLogin(
        normalizedMobile,
        phoneCode
      );

      if (!response?.message) {
        throw new Error("Failed to send OTP. Please try again.");
      }
      if (response.code_reference) {
        setCodeReference(response.code_reference);
      } else {
        throw new Error("No code_reference received from server");
      }

      setOtpSent(true);
      setOtpTimer(60); // 60 seconds timer

      // Start countdown timer
      const interval = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      toast({
        title: "OTP Sent",
        description: response.message,
      });
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.issues[0]?.message ?? "Invalid mobile number",
          variant: "destructive",
        });
      } else {
        const rawMessage = String(
          errorData?.error?.message ??
            errorData?.message ??
            errorData?.detail ??
            error?.message ??
            "Failed to send OTP. Please try again."
        );
        const errorMessage = rawMessage.toLowerCase();

        if (
          errorMessage.includes("not registered") ||
          errorMessage.includes("not found") ||
          errorMessage.includes("does not exist") ||
          errorMessage.includes("sign up")
        ) {
          toast({
            title: "Not signed up",
            description:
              "This phone number is not registered. Ask your admin to re-save the school profile, then try again.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: rawMessage,
            variant: "destructive",
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!otpSent) {
        throw new Error("Please send OTP first");
      }

      // Validate form data
      loginSchema.parse(formData);

      // Verify OTP via Django API
      if (!codeReference) {
        throw new Error("Code reference is missing. Please send OTP again.");
      }

      console.log("🔑 Using code_reference for verification:", codeReference);
      console.log("🔑 OTP being verified:", formData.otp);

      const response = await OTPService.verifyOTPLogin(
        formData.otp,
        codeReference
      );

      // Check if response is valid
      if (!response?.access) {
        throw new Error(
          "Invalid OTP or authentication failed. Please try again."
        );
      }

      // Store authentication tokens
      if (response.access) {
        setLocalAccessToken(response.access);
      }
      if (response.refresh) {
        setLocalRefreshToken(response.refresh);
      }
      if (response.user) {
        // Also fetch school name from /api/users/ if available
        const normalizedMobile = normalizeMobileNumber(formData.mobileNumber);
        let schoolNameFromAPI = "";

        try {
          const usersResponse = await TenantUserService.listTenantUsers(1, 100);
          const usersList =
            usersResponse?.users ?? usersResponse?.results ?? [];

          if (Array.isArray(usersList)) {
            const matchingUser = usersList.find((user: any) => {
              const userPhone = normalizeMobileNumber(user.phone_number ?? "");
              return userPhone === normalizedMobile && userPhone.length > 0;
            });

            if (matchingUser) {
              // Get school name from first_name (where we store it)
              // Also check school_name field if it exists
              schoolNameFromAPI =
                (matchingUser as any).school_name ??
                matchingUser.first_name ??
                "";
              console.log("✅ Found user in /api/users/:", {
                id: matchingUser.id,
                email: matchingUser.email,
                first_name: matchingUser.first_name,
                school_name: (matchingUser as any).school_name,
                phone_number: (matchingUser as any).phone_number,
              });
              console.log("✅ Extracted school name:", schoolNameFromAPI);
            }
          }
        } catch (fetchError) {
          console.warn(
            "⚠️ Could not fetch school name from /api/users/:",
            fetchError
          );
        }

        // Store user info with school name from /api/users/ if available
        const userInfoToStore = {
          ...response.user,
          signup_school_name:
            schoolNameFromAPI ??
            (response.user as any).signup_school_name ??
            "",
          phone_number: normalizedMobile,
        };

        setLocalUserInfo(userInfoToStore);
        console.log(
          "✅ User info stored successfully with school name:",
          schoolNameFromAPI
        );
      }

      // Ensure user exists in architecture-one's /api/users/ system
      // This ensures user has architecture-one credentials to access schemas
      try {
        const userEmail = response.user?.email;

        if (!userEmail) {
          console.warn(
            "⚠️ No email in OTP response, cannot verify user in /api/users/"
          );
        } else {
          console.log("🔍 Checking if user exists in /api/users/ system...");

          // Try to list users and find by email
          // Note: This is a simple approach - in production, you might want a search endpoint
          let userExists = false;
          try {
            const usersResponse = await TenantUserService.listTenantUsers(
              1,
              100
            );

            // Handle different response structures
            // API returns: {success: {...}, users: Array(...), permissions: {...}}
            const usersList =
              usersResponse?.users ??
              usersResponse?.results ??
              usersResponse ??
              [];

            if (Array.isArray(usersList)) {
              const matchingUser = usersList.find(
                (user: any) =>
                  user.email?.toLowerCase() === userEmail.toLowerCase()
              );

              if (matchingUser) {
                console.log("✅ User exists in /api/users/ system");
                userExists = true;
              }
            } else {
              console.warn(
                "⚠️ Unexpected response format from /api/users/:",
                usersResponse
              );
              console.log(
                "📋 Response structure:",
                Object.keys(usersResponse || {})
              );
            }
          } catch (listError) {
            console.warn("⚠️ Could not check user list:", listError);
          }

          // If user doesn't exist, create them
          if (!userExists) {
            console.log("💾 User not found in /api/users/, creating user...");

            const normalizedMobile = normalizeMobileNumber(
              formData.mobileNumber
            );
            console.log("📱 Phone number to store:", normalizedMobile);

            const userPayload: any = {
              email: userEmail,
              password: SCHOOL_DEFAULT_PASSWORD,
              phone_number: normalizedMobile, // CRITICAL: Store phone number for lookup
            };

            // Add optional fields if available from OTP response
            if (response.user?.first_name) {
              userPayload.first_name = response.user.first_name;
            }
            if (response.user?.last_name) {
              userPayload.last_name = response.user.last_name;
            }
            if (response.user?.full_name) {
              userPayload.full_name = response.user.full_name;
            }

            console.log("📋 User payload for /api/users/:", userPayload);

            try {
              const userResponse =
                await TenantUserService.createUser(userPayload);
              console.log(
                "✅ User created in /api/users/ successfully:",
                userResponse
              );
            } catch (createError: any) {
              // If user already exists (race condition), that's okay
              const errorMessage =
                createError?.response?.data?.detail ??
                createError?.response?.data?.message ??
                createError?.message ??
                "";

              console.error("❌ Error creating user:", createError);
              console.error("❌ Error response:", createError?.response?.data);

              if (
                errorMessage.toLowerCase().includes("already exists") ||
                errorMessage.toLowerCase().includes("duplicate") ||
                errorMessage.toLowerCase().includes("unique") ||
                createError?.response?.status === 400
              ) {
                console.log(
                  "ℹ️ User already exists in /api/users/ - that's okay"
                );
              } else {
                console.warn(
                  "⚠️ Could not create user in /api/users/:",
                  createError
                );
                // Don't block login - user already has OTP tokens
              }
            }
          }
        }
      } catch (userError: any) {
        // Don't block login if /api/users/ call fails
        // User already has OTP tokens, so they can proceed
        console.warn("⚠️ Error ensuring user in /api/users/:", userError);
        console.warn(
          "⚠️ User can still proceed - OTP authentication succeeded"
        );
      }

      toast({
        title: "Login Successful",
        description: "Welcome back! Redirecting...",
      });
      navigate(getLink("/school/profile"));
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.issues[0]?.message ?? "Please check your inputs",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message ?? "An error occurred. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto px-4 py-6">
        <Button
          variant="ghost"
          onClick={() => navigate(getLink("/"))}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Button>
      </div>

      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md border-border shadow-strong">
          <CardHeader className="space-y-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-hero">
              <GraduationCap className="h-10 w-10 text-primary-foreground" />
            </div>
            <div>
              <CardTitle className="text-2xl">School Login</CardTitle>
              <CardDescription className="mt-2 text-base">
                Login to your school account to access the Karuna platform
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mobileNumber">
                  Mobile Number <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="mobileNumber"
                    type="tel"
                    placeholder="9876543210"
                    value={formData.mobileNumber}
                    onChange={e => {
                      // Only allow digits
                      const value = e.target.value.replace(/\D/g, "");
                      const normalized = value.slice(0, 10);
                      setFormData({
                        ...formData,
                        mobileNumber: normalized,
                      });
                    }}
                    onKeyDown={e => {
                      // Allow: backspace, delete, tab, escape, enter
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "Escape",
                          "Enter",
                        ].includes(e.key)
                      ) {
                        return;
                      }
                      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                      if (
                        e.ctrlKey &&
                        ["a", "c", "v", "x"].includes(e.key.toLowerCase())
                      ) {
                        return;
                      }
                      // Allow: Arrow keys, Home, End
                      if (
                        [
                          "ArrowLeft",
                          "ArrowRight",
                          "ArrowUp",
                          "ArrowDown",
                          "Home",
                          "End",
                        ].includes(e.key)
                      ) {
                        return;
                      }
                      // Block everything else that's not a digit
                      if (!/^\d$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={e => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData("text");
                      const digitsOnly = pastedText
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      setFormData({
                        ...formData,
                        mobileNumber: digitsOnly,
                      });
                    }}
                    required
                    disabled={otpSent}
                    maxLength={10}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSendOTP}
                    disabled={
                      loading ||
                      otpSent ||
                      !formData.mobileNumber ||
                      formData.mobileNumber.length !== 10 ||
                      otpTimer > 0
                    }
                    className="whitespace-nowrap"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : otpTimer > 0 ? (
                      `Resend (${otpTimer}s)`
                    ) : (
                      "Send OTP"
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  10-digit mobile number starting with 6-9
                </p>
              </div>

              {otpSent && (
                <div className="space-y-2">
                  <Label htmlFor="otp">
                    Enter OTP <span className="text-red-500">*</span>
                  </Label>
                  <InputOTP
                    maxLength={6}
                    value={formData.otp}
                    onChange={value => {
                      // Only allow digits
                      const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
                      setFormData({ ...formData, otp: digitsOnly });
                    }}
                    pattern="^[0-9]*$"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                  <p className="text-xs text-muted-foreground">
                    Enter the 6-digit OTP sent to {formData.mobileNumber}
                  </p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full border-0 bg-gradient-hero"
                disabled={loading || !otpSent || formData.otp.length !== 6}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Login"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Don&apos;t have an account?{" "}
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-primary underline"
                  onClick={() => navigate(getLink("/school/auth"))}
                >
                  Sign up here
                </Button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SchoolLogin;
